/**
 * Centralized query key factory for React Query.
 * 
 * All factory-scoped queries should use these helpers to ensure:
 * 1. Consistent query key structure
 * 2. Proper cache invalidation when factory changes
 * 3. Type safety for query keys
 * 
 * @example
 * // In a component
 * const { data } = useQuery({
 *   queryKey: queryKeys.factoryAreas(selectedFactoryId),
 *   queryFn: () => fetchFactoryAreas(selectedFactoryId!),
 *   enabled: !!selectedFactoryId,
 * });
 * 
 * // When invalidating
 * queryClient.invalidateQueries({ queryKey: queryKeys.factoryAreas(selectedFactoryId) });
 */

// =============================================================================
// Core Factory Structure
// =============================================================================

export const queryKeys = {
  // Factory structure
  factoryAreas: (factoryId: string | null) => 
    ['factory-areas', factoryId] as const,
  
  productionLines: (factoryId: string | null) => 
    ['production-lines', factoryId] as const,
  
  stations: (factoryId: string | null) => 
    ['stations', factoryId] as const,
  
  factoryDetails: (factoryId: string | null) => 
    ['factory-details', factoryId] as const,

  // User management
  factoryUsers: (factoryId: string | null) => 
    ['factory-users', factoryId] as const,
  
  factoryRoles: (factoryId: string | null) => 
    ['factory-roles', factoryId] as const,
  
  rolesWithPermissions: (tenantId: string | null) => 
    ['roles-with-permissions', tenantId] as const,

  userLocations: (userId: string | undefined, factoryId: string | null) => 
    ['user-locations', userId, factoryId] as const,

  // Work instructions & checklists
  workInstructions: (factoryId: string | null) => 
    ['work-instructions', factoryId] as const,
  
  activeChecklists: (factoryId: string | null) => 
    ['active-checklists', factoryId] as const,
  
  checklistDetails: (checklistId: string) => 
    ['checklist-details', checklistId] as const,
  
  recentSubmissions: (factoryId: string | null, userId: string | undefined) => 
    ['recent-submissions', factoryId, userId] as const,
  
  operatorPendingInstructions: (factoryId: string | null, ...params: unknown[]) => 
    ['operator-pending-instructions', factoryId, ...params] as const,
  
  operatorPendingAlerts: (factoryId: string | null, userId: string | undefined) => 
    ['operator-pending-alerts', factoryId, userId] as const,

  // Data sources & mappings
  dataSources: (factoryId: string | null) => 
    ['data-sources', factoryId] as const,
  
  fieldMappings: (factoryId: string | null, ...params: unknown[]) => 
    ['field-mappings', factoryId, ...params] as const,
  
  influxBuckets: (dataSourceId: string) => 
    ['influx-buckets', dataSourceId] as const,
  
  influxMeasurements: (dataSourceId: string, bucket: string) => 
    ['influx-measurements', dataSourceId, bucket] as const,
  
  influxFields: (dataSourceId: string, bucket: string, measurement: string) => 
    ['influx-fields', dataSourceId, bucket, measurement] as const,

  // Competency
  competencies: (factoryId: string | null) => 
    ['competencies', factoryId] as const,
  
  userCompetencies: (factoryId: string | null, userId?: string) => 
    ['user-competencies', factoryId, userId] as const,
  
  operatorCompetencyStats: (factoryId: string | null, userId: string | undefined) => 
    ['operator-competency-stats', factoryId, userId] as const,

  // 8D module
  mod8dClaims: (factoryId: string | null) => 
    ['mod_8d_claims', factoryId] as const,
  
  mod8dClaimDetails: (claimId: string) => 
    ['mod_8d_claim', claimId] as const,
  
  mod8dSuppliers: (factoryId: string | null) => 
    ['mod_8d_suppliers', factoryId] as const,
  
  mod8dSteps: (claimId: string) => 
    ['mod_8d_steps', claimId] as const,
  
  mod8dActions: (claimId: string, stepNumber?: number) => 
    ['mod_8d_actions', claimId, stepNumber] as const,
  
  mod8dComments: (claimId: string) => 
    ['mod_8d_comments', claimId] as const,
  
  mod8dTeamMembers: (claimId: string) => 
    ['mod_8d_team_members', claimId] as const,
  
  mod8dAttachments: (claimId: string) => 
    ['mod_8d_attachments', claimId] as const,
  
  mod8dRootCauses: (claimId: string) => 
    ['mod_8d_root_causes', claimId] as const,

  // Suppliers & quality
  suppliers: (factoryId: string | null) => 
    ['suppliers', factoryId] as const,
  
  incomingInspections: (factoryId: string | null) => 
    ['incoming-inspections', factoryId] as const,
  
  recentInspectionResults: (factoryId: string | null) => 
    ['recent-inspection-results', factoryId] as const,

  // Documents
  documents: (factoryId: string | null) => 
    ['documents', factoryId] as const,

  // Energy
  energyValues: (factoryId: string | null, ...params: unknown[]) => 
    ['energy-values', factoryId, ...params] as const,
  
  energyConfig: (factoryId: string | null) => 
    ['energy-config', factoryId] as const,

  // LPA module
  modLpaQuestions: (factoryId: string | null) => 
    ['mod_lpa_questions', factoryId] as const,

  modLpaTags: (tenantId: string | null) => 
    ['mod_lpa_tags', tenantId] as const,

  modLpaFactorySettings: (factoryId: string | null) => 
    ['mod_lpa_factory_settings', factoryId] as const,

  modLpaAudits: (factoryId: string | null) => 
    ['mod_lpa_audits', factoryId] as const,

  modLpaAuditDetail: (auditId: string | null) => 
    ['mod_lpa_audit', auditId] as const,

  modLpaSchedules: (auditId: string | null) => 
    ['mod_lpa_schedules', auditId] as const,

  modLpaScheduleDetail: (scheduleId: string | null) => 
    ['mod_lpa_schedule', scheduleId] as const,

  modLpaScheduleQuestions: (scheduleId: string | null) => 
    ['mod_lpa_schedule_questions', scheduleId] as const,

  modLpaInstances: (auditId: string | null, tenantId: string | null) =>
    ['mod_lpa_instances', auditId, tenantId] as const,

  modLpaScheduleInstances: (scheduleId: string | null, tenantId: string | null) =>
    ['mod_lpa_schedule_instances_by_schedule', scheduleId, tenantId] as const,

  modLpaInstanceDetail: (instanceId: string | null, tenantId: string | null) =>
    ['mod_lpa_instance', instanceId, tenantId] as const,

  modLpaCompletionDetail: (completionId: string | null, tenantId: string | null) =>
    ['mod_lpa_completion', completionId, tenantId] as const,

  modLpaCompletionSummary: (completionId: string | null, tenantId: string | null) =>
    ['mod_lpa_completion_summary', completionId, tenantId] as const,

  modLpaAuditorInstances: (
    auditorId: string | null,
    tenantId: string | null,
    filters?: { statuses?: string[]; from?: string; to?: string },
  ) => ['mod_lpa_auditor_instances', auditorId, tenantId, filters] as const,

  modLpaResponseAttachments: (responseId: string | null, tenantId: string | null) =>
    ['mod_lpa_response_attachments', responseId, tenantId] as const,
} as const;

// Type for query key values
export type QueryKeys = typeof queryKeys;
